import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import ContactsRail from './ContactsRail';
import MobileRoomList from './MobileRoomList';
import ChatHeader from './ChatHeader';
import MessageThread from './MessageThread';
import MessageComposer, { type ComposerSendPayload } from './MessageComposer';
import EmptyChatState from './EmptyChatState';
import PeerProfilePanel from './PeerProfilePanel';
import GroupInfoPanel from './GroupInfoPanel';
import AddMembersModal from './AddMembersModal';
import GroupSettingsModal from './GroupSettingsModal';
import { useChatRooms } from '../../hooks/chat/useChatRooms';
import { useChatRoom } from '../../hooks/chat/useChatRoom';
import { useChatMessages } from '../../hooks/chat/useChatMessages';
import {
  useDeleteMessage,
  useEditMessage,
  useMarkRead,
  useSendMessage,
} from '../../hooks/chat/useChatMessageActions';
import {
  useCreateRoom,
  useRemoveMember,
} from '../../hooks/chat/useChatRoomActions';
import { useChatRoomSubscription } from '../../hooks/chat/useChatSocket';
import { useCurrentChatIdentity } from '../../hooks/chat/useCurrentChatIdentity';
import type { TenantMember } from '../../types/chat';

export type ChatVariant = 'admin' | 'learner';

interface ChatShellProps {
  variant: ChatVariant;
}

const ChatShell: React.FC<ChatShellProps> = ({ variant }) => {
  const { userId } = useCurrentChatIdentity();
  const [collapsed, setCollapsed] = useState(false);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [groupSettingsOpen, setGroupSettingsOpen] = useState(false);
  const [mobileShowThread, setMobileShowThread] = useState(false);
  // Right-rail panel: closed by default — user opens it manually via the
  // header toggle.
  const [panelOpen, setPanelOpen] = useState(false);

  // Inline new-chat composing mode: when true, the header shows a recipient
  // picker, the chat pane shows the empty state, and the composer is wired
  // to create the room and send the first message in one go.
  const [composing, setComposing] = useState(false);
  const [pickerSelection, setPickerSelection] = useState<TenantMember[]>([]);

  // The global socket lifecycle is mounted at the App root via
  // <ChatLifecycle />. Here we only join/leave the active room channel.
  useChatRoomSubscription(activeRoomId);

  const roomsQuery = useChatRooms();
  const rooms = roomsQuery.data?.rooms ?? [];
  const roomQuery = useChatRoom(activeRoomId);
  const room = roomQuery.data;

  const messagesQuery = useChatMessages(activeRoomId);
  const send = useSendMessage(activeRoomId ?? '');
  const editMsg = useEditMessage(activeRoomId ?? '');
  const deleteMsg = useDeleteMessage(activeRoomId ?? '');
  const markRead = useMarkRead(activeRoomId ?? '');
  const removeMember = useRemoveMember(activeRoomId ?? '');
  const createRoom = useCreateRoom();

  const lastMessageId = useMemo(() => {
    // Skip optimistic / pending messages — their synthetic ids
    // (`01ZZZZZZZZ...`) don't exist on the server yet and would cause
    // the read-receipt write to fail FK validation. Walk back to the
    // most recent server-acknowledged message instead.
    const list = messagesQuery.flatMessages;
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const m = list[i];
      if (!m.pending) return m.id;
    }
    return null;
  }, [messagesQuery.flatMessages]);

  // Mark room as read on a 500ms debounce so rapid lastMessageId churn
  // (e.g. fast-arriving messages while the user reads) coalesces into one
  // POST. Cleared on room change.
  const markReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleMarkRead = (messageId: string) => {
    if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    markReadTimerRef.current = setTimeout(() => {
      markRead.mutate(messageId);
    }, 500);
  };

  useEffect(() => {
    if (!activeRoomId || !lastMessageId) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    scheduleMarkRead(lastMessageId);
    return () => {
      if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoomId, lastMessageId]);

  // Tab regain: refresh the active room (peer read pointers) and re-mark
  // the latest message as read so the badge clears on return.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden || !activeRoomId) return;
      roomQuery.refetch();
      if (lastMessageId) scheduleMarkRead(lastMessageId);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoomId, lastMessageId]);

  // Collapse the contacts rail if the chat-pane column starts out narrow.
  // One-shot on mount only — subsequent resizes don't auto-collapse so the
  // user can reopen the rail and keep it open.
  const paneRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    if (el.clientWidth < 600) setCollapsed(true);
  }, []);

  // Scroll the page to the top whenever the chat screen mounts so the user
  // always lands at the top of the layout.
  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }, []);

  // Auto-open the first room on initial load when nothing else is selected.
  // One-shot — once we've picked the first room, manual navigation (clearing
  // selection, starting a compose, etc.) won't trigger another auto-select.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (composing || activeRoomId) return;
    if (rooms.length === 0) return;
    autoSelectedRef.current = true;
    setActiveRoomId(rooms[0].id);
  }, [rooms, activeRoomId, composing]);

  const handleSelectRoom = (target: { id: string }) => {
    setComposing(false);
    setPickerSelection([]);
    setActiveRoomId(target.id);
    setMobileShowThread(true);
  };

  const handleStartCompose = () => {
    setActiveRoomId(null);
    setComposing(true);
    setPickerSelection([]);
    setMobileShowThread(true);
  };

  const handleCancelCompose = () => {
    setComposing(false);
    setPickerSelection([]);
  };

  const handleSend = async (payload: ComposerSendPayload) => {
    if (composing) {
      if (pickerSelection.length === 0) {
        toast.error('Pick at least one recipient.');
        return;
      }
      const isGroup = pickerSelection.length > 1;
      try {
        const created = await createRoom.mutateAsync({
          type: isGroup ? 'group' : 'direct',
          member_user_ids: pickerSelection.map((m) => m.user_id),
          name: isGroup
            ? `${pickerSelection[0].first_name} & others`
            : undefined,
        });
        setActiveRoomId(created.id);
        setComposing(false);
        setPickerSelection([]);
        // Send the first message after the room is materialized.
        // (The send hook is bound to the previously-empty roomId; we use
        // the freshly returned id directly.)
        const { sendMessage } = await import('../../api/chat');
        try {
          await sendMessage(created.id, {
            body: payload.body,
            attachment_ids: payload.attachment_ids,
          });
        } catch (err) {
          toast.error((err as Error)?.message ?? 'Send failed');
        }
      } catch (err) {
        toast.error((err as Error)?.message ?? 'Failed to start chat');
      }
      return;
    }
    if (!activeRoomId) return;
    send.send({ body: payload.body, attachment_ids: payload.attachment_ids });
  };

  const handleEdit = async (messageId: string) => {
    const current = messagesQuery.flatMessages.find((m) => m.id === messageId);
    const next = window.prompt('Edit message', current?.body ?? '');
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed) return;
    try {
      await editMsg.mutateAsync({ messageId, req: { body: trimmed } });
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Edit failed');
    }
  };

  const handleDelete = async (messageId: string) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await deleteMsg.mutateAsync(messageId);
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Delete failed');
    }
  };

  const isInComposeOrRoom = composing || !!activeRoomId;
  const showRightRail = !!room && !composing && panelOpen;
  const composerEnabled = composing
    ? pickerSelection.length > 0
    : !!activeRoomId;
  const composerTargetRoomId = activeRoomId ?? (composing ? 'pending' : null);

  return (
    <>
      {/* ── Mobile: single column ─────────────────────────────────────── */}
      <div className="lg:hidden h-full">
        {!isInComposeOrRoom || !mobileShowThread ? (
          <MobileRoomList
            rooms={rooms}
            isLoading={roomsQuery.isLoading}
            onSelectRoom={handleSelectRoom}
            onNewChat={handleStartCompose}
          />
        ) : (
          <div className="flex flex-col h-full">
            <button
              type="button"
              onClick={() => {
                setMobileShowThread(false);
                handleCancelCompose();
              }}
              className="self-start mb-1 text-sm text-primary-500 px-4 py-2"
            >
              ← Back
            </button>
            <ChatHeader
              composing={composing}
              pickerSelection={pickerSelection}
              onPickerChange={setPickerSelection}
              onCancelCompose={handleCancelCompose}
              room={room}
            />
            {composing ? (
              <EmptyChatState />
            ) : (
              <MessageThread
                room={room}
                messages={messagesQuery.flatMessages}
                isLoading={messagesQuery.isLoading}
                isFetchingPrevious={messagesQuery.isFetchingPreviousPage}
                hasPrevious={messagesQuery.hasNextPage}
                onLoadPrevious={() => messagesQuery.fetchNextPage()}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            )}
            <MessageComposer
              roomId={composerTargetRoomId}
              disabled={!composerEnabled}
              onSend={handleSend}
            />
          </div>
        )}
      </div>

      {/* ── Desktop: floating rounded card (per Figma 6138-71316) ───────
          card/radius: 16, shadow/card =
            0 12px 24px -4px shadow/12% (#bdbcc91f)
            0  0px  2px  0px shadow/20% (#bdbcc933)
          No explicit border — the inner 2 px shadow acts as the hairline. */}
      <div className="hidden lg:flex h-full w-full rounded-3xl overflow-hidden bg-surface-primary shadow-[0_12px_24px_-4px_rgba(189,188,201,0.12),0_0_0_1px_rgba(189,188,201,0.18),0_0_2px_rgba(189,188,201,0.2)] dark:shadow-[0_12px_24px_-4px_rgba(0,0,0,0.45),0_0_0_1px_rgba(255,255,255,0.06),0_0_2px_rgba(0,0,0,0.5)]">
        <ContactsRail
          rooms={rooms}
          activeRoomId={activeRoomId}
          onSelectRoom={handleSelectRoom}
          onNewChat={handleStartCompose}
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((p) => !p)}
          isLoading={roomsQuery.isLoading}
        />

        <div ref={paneRef} className="flex-1 flex flex-col min-w-0">
          <ChatHeader
            composing={composing}
            pickerSelection={pickerSelection}
            onPickerChange={setPickerSelection}
            room={room}
            panelOpen={panelOpen}
            onTogglePanel={() => setPanelOpen((p) => !p)}
          />

          <div className="flex flex-1 min-h-0">
            <main className="flex-1 flex flex-col min-w-0">
              {isInComposeOrRoom ? (
                <>
                  {composing ? (
                    <EmptyChatState />
                  ) : (
                    <MessageThread
                      room={room}
                      messages={messagesQuery.flatMessages}
                      isLoading={messagesQuery.isLoading}
                      isFetchingPrevious={messagesQuery.isFetchingPreviousPage}
                      hasPrevious={messagesQuery.hasNextPage}
                      onLoadPrevious={() => messagesQuery.fetchNextPage()}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  )}
                  <MessageComposer
                    roomId={composerTargetRoomId}
                    disabled={!composerEnabled}
                    onSend={handleSend}
                  />
                </>
              ) : (
                <EmptyChatState />
              )}
            </main>

            {showRightRail && room && (
              room.type === 'direct' ? (
                <PeerProfilePanel room={room} onClose={() => setPanelOpen(false)} />
              ) : (
                <GroupInfoPanel
                  room={room}
                  onClose={() => setPanelOpen(false)}
                  onAddMembers={() => setAddMembersOpen(true)}
                  onEditGroup={() => setGroupSettingsOpen(true)}
                  onRemoveMember={async (uid) => {
                    try {
                      await removeMember.mutateAsync(uid);
                      toast.success('Member removed');
                    } catch (err) {
                      toast.error((err as Error)?.message ?? 'Failed to remove member');
                    }
                  }}
                />
              )
            )}
          </div>
        </div>
      </div>

      {room?.type === 'group' && (
        <>
          <AddMembersModal
            isOpen={addMembersOpen}
            onClose={() => setAddMembersOpen(false)}
            roomId={room.id}
            excludeUserIds={room.members.map((m) => m.user_id)}
          />
          <GroupSettingsModal
            isOpen={groupSettingsOpen}
            onClose={() => setGroupSettingsOpen(false)}
            room={room}
          />
        </>
      )}

      {/* Reference `userId` and `variant` to satisfy unused-vars lint */}
      <span className="hidden" data-self-id={userId ?? ''} data-variant={variant} />
    </>
  );
};

export default ChatShell;
